// @ts-nocheck
/**
 * Addressbook API Types - Zod Schemas & DTOs for Strapi Integration
 * Based on Strapi contentTypes.d.ts
 */

import { z } from '@ufc/core';

// ============================================================================
// Strapi Response Meta
// ============================================================================

export const strapiPaginationSchema = z.object({
  page: z.number(),
  pageSize: z.number(),
  pageCount: z.number(),
  total: z.number(),
});

export type StrapiPagination = z.infer<typeof strapiPaginationSchema>;

export const strapiMetaSchema = z.object({
  pagination: strapiPaginationSchema.optional(),
});

export type StrapiMeta = z.infer<typeof strapiMetaSchema>;

// ============================================================================
// Localization DTO (Strapi i18n)
// ============================================================================

export const localizationDtoSchema = z.object({
  id: z.number().optional(),
  documentId: z.string().optional(),
  locale: z.string(),
  name: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  stateProvince: z.string().nullable().optional(),
});

export type LocalizationDto = z.infer<typeof localizationDtoSchema>;

// ============================================================================
// Country DTO (api::country.country)
// ============================================================================

export const countryDtoSchema = z.object({
  id: z.number(),
  documentId: z.string(),
  code: z.string(), // max 3
  name: z.string(), // max 100
  isActive: z.boolean().default(true),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type CountryDto = z.infer<typeof countryDtoSchema>;

// ============================================================================
// City DTO (api::city.city)
// ============================================================================

export const factorySummaryDtoSchema = z.object({
  id: z.number(),
  documentId: z.string().optional(),
  code: z.string().optional(),
  // Factory name can be a string or a localized object {en: string, ar: string}
  name: z.union([z.string(), z.record(z.string())]).optional(),
});

export type FactorySummaryDto = z.infer<typeof factorySummaryDtoSchema>;

// ============================================================================
// Region DTO (api::region.region)
// ============================================================================

export const regionDtoSchema = z.object({
  id: z.number(),
  documentId: z.string(),
  code: z.string(),
  name: z.string(),
  description: z.string().nullable().optional(),
  isActive: z.boolean().default(true),
  country: countryDtoSchema.nullable().optional(),
  factories: z
    .array(factorySummaryDtoSchema)
    .nullable()
    .optional()
    .transform((value) => value ?? []),
  locale: z.string().optional(),
  localizations: z.array(localizationDtoSchema).optional(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
});

export type RegionDto = z.infer<typeof regionDtoSchema>;

export const cityDtoSchema = z.object({
  id: z.number(),
  documentId: z.string(),
  code: z.string(), // max 50
  name: z.string(), // max 100
  stateProvince: z.string().nullable().optional(), // max 100
  region: regionDtoSchema.nullable().optional(),
  isActive: z.boolean().default(true),
  country: countryDtoSchema.nullable().optional(),
  locale: z.string().optional(),
  localizations: z.array(localizationDtoSchema).optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type CityDto = z.infer<typeof cityDtoSchema>;

// ============================================================================
// Tag Category DTO (api::tag-category.tag-category)
// ============================================================================

export const tagCategoryDtoSchema = z.object({
  id: z.number(),
  documentId: z.string(),
  code: z.string(), // max 50
  name: z.string(), // max 255
  description: z.string().nullable().optional(),
  icon: z.string().nullable().optional(), // max 50
  color: z.string().nullable().optional(), // max 7
  sortOrder: z.number().default(0),
  isActive: z.boolean().default(true),
  allowsMultiple: z.boolean().default(true),
  isRequired: z.boolean().default(false),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type TagCategoryDto = z.infer<typeof tagCategoryDtoSchema>;

// ============================================================================
// Tag DTO (api::tag.tag)
// ============================================================================

export const tagDtoSchema = z.object({
  id: z.number(),
  documentId: z.string(),
  code: z.string(), // max 100
  name: z.string(), // max 255
  description: z.string().nullable().optional(),
  icon: z.string().nullable().optional(), // max 50
  color: z.string().nullable().optional(), // max 7
  sortOrder: z.number().default(0),
  isActive: z.boolean().default(true),
  category: tagCategoryDtoSchema.nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type TagDto = z.infer<typeof tagDtoSchema>;

// ============================================================================
// Location Type DTO (api::location-type.location-type)
// ============================================================================

export const locationTypeDtoSchema = z.object({
  id: z.number(),
  documentId: z.string(),
  code: z.string(), // max 50
  name: z.string(), // max 255
  description: z.string().nullable().optional(),
  icon: z.string().nullable().optional(), // max 50
  color: z.string().nullable().optional(), // max 7
  sortOrder: z.number().default(0),
  isActive: z.boolean().default(true),
  requiresParent: z.boolean().default(false),
  requiresCoordinates: z.boolean().default(true),
  requiresLoadingCapabilities: z.boolean().default(false),
  requiresOperatingHours: z.boolean().default(true),
  requiresPermits: z.boolean().default(false),
  allowedChildTypes: z.array(z.string()).nullable().optional(),
  allowedOwnerTypes: z.array(z.string()).nullable().optional(),
  hierarchyLevelRestriction: z.number().nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type LocationTypeDto = z.infer<typeof locationTypeDtoSchema>;

// ============================================================================
// Tenant DTO (api::tenant.tenant)
// ============================================================================

export const tenantStatusSchema = z.enum([
  'pending',
  'active',
  'inactive',
  'suspended',
]);

export const tenantDtoSchema = z.object({
  id: z.number(),
  documentId: z.string(),
  code: z.string(), // max 50
  name: z.string(), // max 255
  legalName: z.string().nullable().optional(), // max 255
  taxId: z.string().nullable().optional(), // max 50
  vatNumber: z.string().nullable().optional(), // max 50
  registrationNumber: z.string().nullable().optional(), // max 100
  status: tenantStatusSchema.default('pending'),
  settings: z.record(z.string(), z.unknown()).nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type TenantDto = z.infer<typeof tenantDtoSchema>;

// ============================================================================
// Contact DTO (api::contact.contact)
// ============================================================================

export const contactTypeSchema = z.enum([
  'gate_manager',
  'dock_supervisor',
  'security',
  'primary_contact',
  'emergency_contact',
]);

export type ContactTypeDto = z.infer<typeof contactTypeSchema>;

export const contactDtoSchema = z.object({
  id: z.number(),
  documentId: z.string(),
  name: z.string(), // max 255
  title: z.string().nullable().optional(), // max 100
  email: z.string().nullable().optional(), // max 255
  phone: z.string().nullable().optional(), // max 50
  mobile: z.string().nullable().optional(), // max 50
  fax: z.string().nullable().optional(), // max 50
  contactType: contactTypeSchema,
  preferredLanguage: z.string().nullable().optional(), // max 10
  notes: z.string().nullable().optional(),
  isPrimary: z.boolean().default(false),
  isActive: z.boolean().default(true),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type ContactDto = z.infer<typeof contactDtoSchema>;

// ============================================================================
// Location DTO (api::location.location)
// ============================================================================

export const locationStatusSchema = z.enum(['active', 'inactive']);

export type LocationStatusDto = z.infer<typeof locationStatusSchema>;

const locationLocalizationEntrySchema = z
  .object({
    locale: z.string(),
    name: z.string().optional(),
    addressLine1: z.string().nullable().optional(),
    addressLine2: z.string().nullable().optional(),
  })
  .passthrough();

// @ts-ignore
export const locationDtoSchema = z.object({
  id: z.number(),
  documentId: z.string(),
  code: z.string(), // max 100
  name: z.string(), // max 255
  addressLine1: z.string().nullable().optional(), // max 255
  addressLine2: z.string().nullable().optional(), // max 255
  postalCode: z.string().nullable().optional(), // max 20
  timezone: z.string().nullable().optional(), // max 50
  latitude: z.number().nullable().optional(),
  longitude: z.number().nullable().optional(),
  status: locationStatusSchema.default('active'),
  metadata: z.record(z.string(), z.unknown()).nullable().optional(),
  locale: z.string().optional(),
  localizations: z.array(locationLocalizationEntrySchema).optional(),
  // Relations (populated)
  city: cityDtoSchema.nullable().optional(),
  country: countryDtoSchema.nullable().optional(),
  locationType: locationTypeDtoSchema.nullable().optional(),
  parent: z
    .lazy(() => locationDtoSchema.partial())
    .nullable()
    .optional(),
  tenant: tenantDtoSchema.nullable().optional(),
  tags: z.array(tagDtoSchema).nullable().optional(),
  // Timestamps
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type LocationDto = z.infer<typeof locationDtoSchema>;

// ============================================================================
// Strapi Response Wrappers
// ============================================================================

export const strapiSingleResponseSchema = <T extends z.ZodTypeAny>(
  dataSchema: T,
) =>
  z.object({
    data: dataSchema,
    meta: strapiMetaSchema.optional(),
  });

export const strapiListResponseSchema = <T extends z.ZodTypeAny>(
  dataSchema: T,
) =>
  z.object({
    data: z.array(dataSchema),
    meta: strapiMetaSchema.optional(),
  });

// Location responses
export const locationResponseSchema =
  strapiSingleResponseSchema(locationDtoSchema);
export const locationListResponseSchema =
  strapiListResponseSchema(locationDtoSchema);
export const locationStatsDtoSchema = z.object({
  total: z.number().int().nonnegative(),
  active: z.number().int().nonnegative(),
  inactive: z.number().int().nonnegative(),
  byType: z.record(z.string(), z.number().int().nonnegative()),
});
export const locationStatsResponseSchema = z.object({
  data: locationStatsDtoSchema,
});

export type LocationResponse = z.infer<typeof locationResponseSchema>;
export type LocationListResponse = z.infer<typeof locationListResponseSchema>;
export type LocationStatsDto = z.infer<typeof locationStatsDtoSchema>;
export type LocationStatsResponse = z.infer<typeof locationStatsResponseSchema>;

// Location type responses
export const locationTypeResponseSchema = strapiSingleResponseSchema(
  locationTypeDtoSchema,
);
export const locationTypeListResponseSchema = strapiListResponseSchema(
  locationTypeDtoSchema,
);

export type LocationTypeResponse = z.infer<typeof locationTypeResponseSchema>;
export type LocationTypeListResponse = z.infer<
  typeof locationTypeListResponseSchema
>;

// City responses
export const cityListResponseSchema = strapiListResponseSchema(cityDtoSchema);
export type CityListResponse = z.infer<typeof cityListResponseSchema>;

// Region responses
export const regionListResponseSchema =
  strapiListResponseSchema(regionDtoSchema);
export type RegionListResponse = z.infer<typeof regionListResponseSchema>;

// Country responses
export const countryListResponseSchema =
  strapiListResponseSchema(countryDtoSchema);
export type CountryListResponse = z.infer<typeof countryListResponseSchema>;

// Tag responses
export const tagListResponseSchema = strapiListResponseSchema(tagDtoSchema);
export type TagListResponse = z.infer<typeof tagListResponseSchema>;

// ============================================================================
// Request DTOs
// ============================================================================

export const createLocationRequestSchema = z.object({
  code: z.string().min(1).max(100),
  name: z.string().min(1).max(255),
  addressLine1: z.string().max(255).optional(),
  addressLine2: z.string().max(255).optional(),
  postalCode: z.string().max(20).optional(),
  timezone: z.string().max(50).optional(),
  latitude: z.number().optional(),
  longitude: z.number().optional(),
  status: locationStatusSchema.optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
  // Relation IDs
  city: z.number().optional(),
  country: z.number().optional(),
  locationType: z.number().optional(),
  parent: z.number().optional(),
  tenant: z.number().optional(),
  tags: z.array(z.number()).optional(),
});

export type CreateLocationRequest = z.infer<typeof createLocationRequestSchema>;

export const updateLocationRequestSchema =
  createLocationRequestSchema.partial();

export type UpdateLocationRequest = z.infer<typeof updateLocationRequestSchema>;

// ============================================================================
// API Endpoints
// ============================================================================

export const STRAPI_ENDPOINTS = {
  // Locations
  LOCATIONS: '/api/locations',
  LOCATIONS_STATS: '/api/locations/stats',
  LOCATION_BY_ID: (documentId: string) => `/api/locations/${documentId}`,
  // Location Types
  LOCATION_TYPES: '/api/location-types',
  LOCATION_TYPE_BY_ID: (documentId: string) =>
    `/api/location-types/${documentId}`,
  // Cities
  CITIES: '/api/cities',
  CITY_BY_ID: (documentId: string) => `/api/cities/${documentId}`,
  // Regions
  REGIONS: '/api/regions',
  REGION_BY_ID: (documentId: string) => `/api/regions/${documentId}`,
  // Countries
  COUNTRIES: '/api/countries',
  COUNTRY_BY_ID: (documentId: string) => `/api/countries/${documentId}`,
  // Tags
  TAGS: '/api/tags',
  TAG_BY_ID: (documentId: string) => `/api/tags/${documentId}`,
  // Contacts
  CONTACTS: '/api/contacts',
  CONTACT_BY_ID: (documentId: string) => `/api/contacts/${documentId}`,
} as const;

// ============================================================================
// Query Builder Helpers
// ============================================================================

export interface LocationQueryParams {
  // Filters
  status?: 'active' | 'inactive';
  locationTypeCode?: string;
  cityId?: number;
  countryId?: number;
  countryCode?: string;
  tenantId?: number;
  /** When true, only return locations with no tenant (global/shared locations) */
  noTenant?: boolean;
  search?: string;
  // Population
  populate?:
    | ('city' | 'country' | 'locationType' | 'tags' | 'parent' | 'tenant')[]
    | null;
  // Pagination
  page?: number;
  pageSize?: number;
  // Sorting
  sort?: string;
}

export function buildLocationQuery(
  params: LocationQueryParams,
): Record<string, string | boolean> {
  const query: Record<string, string | boolean> = {};

  // Filters
  if (params.status) {
    query['filters[status][$eq]'] = params.status;
  }
  if (params.locationTypeCode) {
    query['filters[locationType][code][$eq]'] = params.locationTypeCode;
  }
  if (params.cityId) {
    query['filters[city][id][$eq]'] = String(params.cityId);
  }
  if (params.countryId) {
    query['filters[country][id][$eq]'] = String(params.countryId);
  }
  if (params.countryCode) {
    query['filters[country][code][$eq]'] = params.countryCode;
  }
  if (params.tenantId) {
    query['filters[tenant][id][$eq]'] = String(params.tenantId);
  } else if (params.noTenant) {
    query['filters[tenant][$null]'] = 'true';
  }
  if (params.search) {
    query['filters[$or][0][name][$containsi]'] = params.search;
    query['filters[$or][1][code][$containsi]'] = params.search;
    query['filters[$or][2][addressLine1][$containsi]'] = params.search;
  }

  // Population
  if (params.populate !== null) {
    if (params.populate && params.populate.length > 0) {
      params.populate.forEach((rel, index) => {
        query[`populate[${index}]`] = rel;
      });
    } else {
      // Default population with explicit fields (Strapi 5 compatible - no wildcards on nested relations)
      query['populate[city][fields][0]'] = 'id';
      query['populate[city][fields][1]'] = 'documentId';
      query['populate[city][fields][2]'] = 'code';
      query['populate[city][fields][3]'] = 'name';
      query['populate[city][fields][4]'] = 'stateProvince';
      query['populate[city][fields][5]'] = 'isActive';
      query['populate[city][populate][country][fields][0]'] = 'id';
      query['populate[city][populate][country][fields][1]'] = 'documentId';
      query['populate[city][populate][country][fields][2]'] = 'code';
      query['populate[city][populate][country][fields][3]'] = 'name';
      query['populate[city][populate][region][fields][0]'] = 'id';
      query['populate[city][populate][region][fields][1]'] = 'documentId';
      query['populate[city][populate][region][fields][2]'] = 'code';
      query['populate[city][populate][region][fields][3]'] = 'name';
      query['populate[country][fields][0]'] = 'id';
      query['populate[country][fields][1]'] = 'documentId';
      query['populate[country][fields][2]'] = 'code';
      query['populate[country][fields][3]'] = 'name';
      query['populate[locationType][fields][0]'] = 'id';
      query['populate[locationType][fields][1]'] = 'documentId';
      query['populate[locationType][fields][2]'] = 'code';
      query['populate[locationType][fields][3]'] = 'name';
      query['populate[tenant][fields][0]'] = 'id';
      query['populate[tenant][fields][1]'] = 'documentId';
      query['populate[tenant][fields][2]'] = 'code';
      query['populate[tenant][fields][3]'] = 'name';
    }
  }

  // Pagination
  if (params.page) {
    query['pagination[page]'] = String(params.page);
  }
  if (params.pageSize) {
    query['pagination[pageSize]'] = String(params.pageSize);
  }

  // Sorting
  if (params.sort) {
    query['sort'] = params.sort;
  }

  return query;
}

// ============================================================================
// Validation Helpers
// ============================================================================

export function validateLocationDto(data: unknown): LocationDto {
  return locationDtoSchema.parse(data);
}

export function validateLocationListResponse(
  data: unknown,
): LocationListResponse {
  return locationListResponseSchema.parse(data);
}

export function validateLocationStatsResponse(
  data: unknown,
): LocationStatsResponse {
  return locationStatsResponseSchema.parse(data);
}

export function validateLocationTypeDto(data: unknown): LocationTypeDto {
  return locationTypeDtoSchema.parse(data);
}

export function validateCityDto(data: unknown): CityDto {
  return cityDtoSchema.parse(data);
}

export function validateCountryDto(data: unknown): CountryDto {
  return countryDtoSchema.parse(data);
}

export function validateTagDto(data: unknown): TagDto {
  return tagDtoSchema.parse(data);
}
